"use client";

import React from "react";
import { Upload, ArrowRight, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function ShareVisionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in duration-700">
        <div className="size-16 rounded-md bg-secondary/10 flex items-center justify-center border border-secondary/30">
          <CircleCheck className="size-8 text-secondary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-2xl md:text-3xl font-bold tracking-tight uppercase text-foreground">
            Request Received
          </h3>
          <p className="text-muted-foreground font-default text-sm max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-md border-secondary/50 hover:bg-secondary/10 hover:text-secondary hover:border-secondary transition-all font-default uppercase tracking-widest text-xs">
          Send Another Request
        </Button>
      </div>;
  }
  const inputClasses = "rounded-md border-0 border-b border-border bg-transparent focus-visible:border-secondary focus-visible:ring-0 focus-visible:ring-offset-0 transition-all duration-300 h-12 shadow-none";
  const labelClasses = "font-default text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground mb-3 block";
  return <Form onSubmit={handleSubmit} className="space-y-8 md:space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander von Luxury" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="font-default text-[10px] opacity-50">
                As it appears on your business registration
              </FormDescription>
              <FormMessage className="font-default text-[10px] uppercase text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Professional Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="contact@yourhotel.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-default text-[10px] uppercase text-destructive" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+420 123 456 789" className={inputClasses} {...field} />
              </FormControl>
              <FormDescription className="font-default text-[10px] opacity-50">
                Optional – for a direct consultation
              </FormDescription>
              <FormMessage className="font-default text-[10px] uppercase text-destructive" />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>
                Property Name / URL
              </FormLabel>
              <FormControl>
                <Input placeholder="Grand Hotel Prague" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="font-default text-[10px] uppercase text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_type" rules={{
      required: "Please select a property type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Property Category</FormLabel>
            <FormControl>
              <div className="relative">
                <select className={`flex w-full min-w-0 appearance-none px-3 py-1 text-base shadow-none outline-none md:text-sm ${inputClasses} text-foreground`} {...field}>
                  <option value="" disabled className="bg-background text-muted-foreground">
                    Select your property type
                  </option>
                  {["Luxury Hotel", "Boutique Guesthouse", "Serviced Apartment", "Airbnb / Short-term Rental", "Real Estate Portfolio", "Other"].map(opt => <option key={opt} value={opt} className="bg-background text-foreground py-2">
                      {opt}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-muted-foreground">
                  <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage className="font-default text-[10px] uppercase text-destructive" />
          </FormItem>} />

      <FormField control={control} name="description" rules={{
      required: "Project description is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className={labelClasses}>Enhancement Goals</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your property and what you'd like to achieve with AI enhancement..." className={`min-h-[160px] resize-y ${inputClasses}`} {...field} />
            </FormControl>
            <FormMessage className="font-default text-[10px] uppercase text-destructive" />
          </FormItem>} />

      <FormField control={control} name="reference_files" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className={labelClasses}>
              Current Property Photos (optional)
            </FormLabel>
            <FormControl>
              <div className="relative group">
                <div className="border border-dashed border-border bg-background/30 hover:bg-secondary/5 p-8 flex flex-col items-center justify-center gap-4 cursor-pointer group-hover:border-secondary/50 transition-all duration-300 min-h-[120px] rounded-md">
                  <Upload className="size-6 text-muted-foreground group-hover:text-secondary transition-colors" />
                  <div className="text-center space-y-1">
                    <span className="font-default text-xs text-foreground block">
                      Click to upload or drag and drop
                    </span>
                    <span className="font-default text-[10px] text-muted-foreground block">
                      JPG, PNG or PDF (max. 10MB)
                    </span>
                  </div>
                </div>
                <Input type="file" multiple className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={e => {
            onChange(e.target.files);
          }} {...field} value={undefined} />
              </div>
            </FormControl>
            <FormDescription className="font-default text-[10px] opacity-50">
              Upload current photos for a free AI-enhanced sample.
            </FormDescription>
            <FormMessage className="font-default text-[10px] uppercase text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 flex items-start gap-3 rounded-md">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive font-default text-xs leading-relaxed">
            {submitError}
          </p>
        </div>}

      <div className="pt-6 border-t border-border">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default uppercase tracking-widest text-sm group relative overflow-hidden shadow-md">
          <span className="relative z-10 flex items-center justify-center gap-2">
            {isSubmitting ? <>
                <Loader className="animate-spin size-4" />
                Processing Request...
              </> : <>
                Elevate My Property
                <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
              </>}
          </span>
          <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
        </Button>
      </div>
    </Form>;
}
export default function ElevateYourProperty() {
  return <section data-section-id="222523" id="elevate-your-property" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden selection:bg-secondary/30 selection:text-secondary-foreground">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1px] h-full bg-border/20" />
        <div className="absolute top-1/4 left-0 w-full h-[1px] bg-border/20" />
        <div className="absolute top-3/4 left-0 w-full h-[1px] bg-border/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-lg blur-[120px] opacity-30" />
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="max-w-[600px] mx-auto">
          <div className="mb-16 md:mb-20 space-y-6 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-secondary/30 bg-background/50 backdrop-blur-sm rounded-md">
              <span className="size-1.5 bg-secondary rounded-lg animate-pulse" />
              <span className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                Concierge Available
              </span>
            </div>
            <h2 className="font-default text-4xl md:text-6xl font-bold tracking-tighter uppercase leading-[0.9]">
              Elevate Your <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-secondary/60">
                Property
              </span>
            </h2>
            <p className="font-default text-sm text-muted-foreground max-w-md leading-relaxed">
              Ready to redefine your property's potential? Provide your details below, and our AI-enhancement engine will begin transforming your visual presentation.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 border border-border bg-card/40 backdrop-blur-xl pointer-events-none hidden md:block rounded-md shadow-sm" />
            
            <FormProvider formKey="elevate_property_form_home" formId="elevate_property_form_home" sectionName="elevate-your-property" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            project_type: "",
            description: "",
            reference_files: undefined
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            project_type: {
              required: true
            },
            description: {
              required: true
            }
          }} submitText="Elevate My Property" successMessage="Your property details have been received. Our specialists will review your photos and contact you with a transformation proposal shortly." className="relative z-10">
              <ShareVisionForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}